import React, { useContext } from "react";
import parse from "html-react-parser";
import VoucherServices, {
  Party_Tax,
  Voucher_Type,
} from "../../../../Services/VoucherServices";
import { Drawer } from "antd";
import { useClient } from "../../../../Context/ClientContext";
import { notificationContext } from "../../../../Common/Routing/PageRoute";

import {
  Page,
  Text,
  View,
  Document,
  PDFViewer,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { Table, TR, TH, TD } from "@ag-media/react-pdf-table";
import { dayjs } from "../../../../../Utilities/dayjs";
import { Utils } from "../../../../../Utilities/Utils";
import { useUser } from "../../../../../Context/UserContext";
import { IAddressDetails } from "../../../../Services/SettingService";
import { TypeOfAddress } from "../../../../Services/LedgerService";
import { renderNode } from "./SalesInvoicePDF";

Font.register({
  family: "Arial",
  fonts: [
    {
      src: "/fonts/arial.ttf",
    },
    {
      fontStyle: "bold",
      src: "/fonts/arialbd.ttf",
    },
  ],
});

Font.register({
  family: "Times New Roman",
  fonts: [
    {
      src: "/fonts/times.ttf",
    },
    {
      fontStyle: "bold",
      src: "/fonts/timesbd.ttf",
    },
  ],
});

Font.register({
  family: "Lucida Sans",
  fonts: [
    {
      src: "/fonts/LSANS.ttf",
    },
    {
      fontStyle: "bold",
      src: "/fonts/LSANSD.ttf",
    },
  ],
});

Font.register({
  family: "Georgia",
  fonts: [
    {
      src: "/fonts/georgia.ttf",
    },
    {
      fontStyle: "bold",
      src: "/fonts/georgiab.ttf",
    },
  ],
});

const PurchaseBillPDF: React.FC<{
  open: boolean;
  onCancel: any;
  voucherId?: any;
  type?: any;
}> = (props) => {
  const { currentClient, companySettings } = useClient();
  const { getAllIndStates } = useUser();
  const stateOptions: any = getAllIndStates();
  const { openNotification } = useContext(notificationContext);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<any>({});

  const templeteAddressSelected: IAddressDetails =
    currentClient?.addresses?.find(
      (x) =>
        x.addressName ===
        companySettings?.setting?.templateSettings?.[0]?.companyTS
          ?.defaultAddress
    ) ?? currentClient?.addresses
      ? currentClient?.addresses[0]
      : {
          type: TypeOfAddress.RegisteredAddress,
          addressName: "",
          address: {
            city: "",
            state: -1,
            pincode: "",
            country: "",
            building: "",
            street: "",
            landmark: "",
            district: "",
          },
        };

  React.useEffect(() => {
    if (props.voucherId) {
      setLoading(true);
      VoucherServices.getVoucharById(
        currentClient?.id!,
        props.voucherId,
        props?.type
      )
        .then((res: any) => {
          let itemTotalTaxableAmount = res?.result?.items?.reduce(
            (acc: any, rec: any) => acc + rec?.amount?.grossAmount,
            0
          );
          let itemTotalTaxAmt = res?.result?.items?.reduce(
            (acc: any, rec: any) => acc + rec?.amount?.itemTaxAmt,
            0
          );
          let otherExpTotalTaxableAmount =
            res?.result?.freightInsLedgers?.reduce(
              (acc: number, rec: any) =>
                acc +
                Math.abs(
                  rec?.partyOrTax !== Party_Tax.Discount ? rec?.amount : 0
                ),
              0
            );
          let otherExpTotalTaxAmt = res?.result?.freightInsLedgers?.reduce(
            (acc: number, rec: any) =>
              acc +
              (rec?.partyOrTax !== Party_Tax.Discount ? rec?.gstAmount : 0),
            0
          );
          let totalItems = res?.result?.items;
          if (totalItems?.length < 11) {
            const itemsToAdd =
              10 -
              (res?.result?.freightInsLedgers
                ? res?.result?.freightInsLedgers?.length
                : 0) -
              (totalItems?.length || 0);
            const emptyItems = Array.from({ length: itemsToAdd }, () => ({
              id: "",
              name: "",
              amount: {},
            }));
            totalItems.push(...emptyItems);
          }
          setData({
            date: dayjs(res?.result?.date).format("DD-MM-YYYY"),
            number: res?.result?.number,
            narration: res?.result?.narration,
            effectiveLedger: res?.result?.effectiveLedger[0]?.ledgerName,
            effectiveLedgerId: res?.result?.effectiveLedger[0]?.id,
            partyLedger: res?.result?.partyLedger,
            freightInsLedgers: res?.result?.freightInsLedgers?.filter(
              (led: any) =>
                led?.partyOrTax !== Party_Tax.Discount &&
                led?.partyOrTax !== Party_Tax.RoundOff
            ),
            ledgerName: res?.result?.partyLedger[0].ledgerName,
            instantPayment: res.result?.instantPayment,
            voucherNetAmount: res?.result?.amountSummary?.totalInvoiceAmount,
            amountInWords:
              res?.result?.amountSummary?.totalInvoiceAmountInWords,
            voucherTitle: res?.result?.voucherTitle,
            billingAddress: res?.result?.billingAddress?.address,
            shippingAddress: res?.result?.shippingAddress?.address,
            items: totalItems,
            isGSTVoucher: res?.result?.isGSTVoucher,
            isStateSame: res?.result?.isStateSame,
            amountSummary: res?.result?.amountSummary,
            roundOff: res?.result?.amountSummary?.roundOff,
            cashDiscount: res?.result?.amountSummary?.cashDiscount,
            taxDetail: res?.result?.taxSummary?.taxDetail,
            supplyState: res?.result?.supplyState,
            termsNConditions: res?.result?.termsNConditions,
            purchaseOrderRef: res?.result?.purchaseOrderRef,
            name: res?.result?.purchaseOrderRef?.name,
            // Item Total
            totalQty: res?.result?.amountSummary?.totalQty,
            itemTotalTaxableAmount: itemTotalTaxableAmount,
            itemTotalTaxAmt: itemTotalTaxAmt,
            // Other Expenses Total
            otherExpTotalTaxableAmount: otherExpTotalTaxableAmount,
            otherExpTotalTaxAmt: otherExpTotalTaxAmt,
            // Grand Total
            grandTaxableAmount: res?.result?.taxSummary?.grandTaxableAmount,
            grandIGSTAmount: res?.result?.taxSummary?.grandIGSTAmount,
            grandCGSTAmount: res?.result?.taxSummary?.grandCGSTAmount,
            grandSGSTAmount: res?.result?.taxSummary?.grandSGSTAmount,
            grandTotalAmount: res?.result?.taxSummary?.grandTotalAmount,
            totalInvoiceAmount: res?.result?.amountSummary?.totalInvoiceAmount,
            totalInvoiceAmountInWords:
              res?.result?.amountSummary?.totalInvoiceAmountInWords,
            totalItemGSTAmt: res?.result?.amountSummary?.totalItemGSTAmt,
          });
          console.log("reddddddddddddd", res);
          setLoading(false);
        })
        .catch((ex: any) => {
          openNotification("error", ex.message);
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClient?.id, props.voucherId]);

  // const showData: any = companySettings?.setting?.templateSettings?.[0];

  const style = StyleSheet.create({
    widths: {
      width: "100%",
    },
    mergedMainTDWithBorder: {
      display: "flex",
      flexDirection: "column",
      padding: 0,
      height: "auto",
    },
    mergedMainTD: {
      display: "flex",
      flexDirection: "column",
      padding: 0,
      borderBottom: "none",
    },
    mergedMainTDWithoutTopnBottomBorder: {
      display: "flex",
      flexDirection: "column",
      padding: 0,
      borderBottom: 0,
      borderTop: 0,
    },
    mergedMainRow: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      justifyContent: "flex-end",
      borderBottom: "none",
      padding: 0,
    },
    mergedMainRowWithoutTopnBottomBorder: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      justifyContent: "flex-end",
      borderBottom: 0,
      borderTop: 0,
      padding: 0,
    },
    summaryTableStyleRight: {
      padding: "2px",
      justifyContent: "flex-end",
    },
    summaryTableStyleLeft: {
      padding: "2px",
      justifyContent: "flex-start",
    },
    summaryTableStyleCenter: {
      padding: "2px",
      justifyContent: "center",
    },
  });

  console.log("Data", data);

  return (
    <React.Fragment>
      <Drawer
        open={props.open}
        width={"50%"}
        style={{ maxHeight: `calc(100%)`, overflowY: "auto" }}
        onClose={() => props.onCancel()}
        // maskClosable={false}
        // closable={false}
        title="Preview"
        loading={loading}
      >
        <PDFViewer
          style={{
            height: "800px",
            width: "100%",
          }}
        >
          <Document title="Document">
            <Page
              size={"A4"}
              style={{
                width: "100%",
                height: "100%",
                // padding: "15px",
              }}
              wrap={false}
              orientation="portrait"
            >
              <View
                style={{
                  marginTop: 30,
                  marginBottom: 30,
                  marginLeft: 40,
                  marginRight: 30,
                  height: "100%",
                }}
              >
                <div
                  style={{
                    fontFamily: "Arial",
                    fontSize: 10,
                  }}
                >
                  {/* Header - Company Detail and Voucher Title */}
                  <View
                    style={{
                      padding: "5px 0px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* Company Name, Logo and Address */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Company Logo  */}
                        {/* {showData?.companyTS?.showLogo && <div></div>} */}
                        {/* Company Name and Address */}
                        <div>
                          {/* Company Name */}
                          <div>
                            <Text
                              style={{
                                fontSize: 18,
                              }}
                            >
                              {currentClient?.businessName}
                            </Text>
                          </div>
                          {/* Company Address */}
                          <div>
                            <div>
                              {templeteAddressSelected?.address?.building !==
                              null ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "5px",
                                  }}
                                >
                                  <Text>
                                    {
                                      templeteAddressSelected?.address
                                        ?.building!
                                    }
                                    {", "}
                                  </Text>
                                  {templeteAddressSelected?.address?.street !==
                                  null ? (
                                    <Text>
                                      {templeteAddressSelected?.address?.street}
                                    </Text>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              ) : (
                                ""
                              )}

                              {templeteAddressSelected?.address?.landmark !==
                                null && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                  }}
                                >
                                  <Text>
                                    {templeteAddressSelected?.address?.landmark}
                                  </Text>
                                </div>
                              )}

                              {templeteAddressSelected?.address?.city !==
                              null ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                  }}
                                >
                                  <Text>
                                    {templeteAddressSelected?.address?.city}
                                  </Text>
                                  {templeteAddressSelected?.address
                                    ?.district !== null ? (
                                    <Text>
                                      {", "}
                                      {
                                        templeteAddressSelected?.address
                                          ?.district
                                      }
                                    </Text>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              ) : (
                                ""
                              )}
                            </div>

                            <div>
                              <Text>
                                {templeteAddressSelected?.address?.state !==
                                null ? (
                                  <>
                                    <Text>
                                      <Text
                                        style={{
                                          fontStyle: "bold",
                                        }}
                                      >
                                        State:{" "}
                                      </Text>
                                      {Object.entries(stateOptions)?.map(
                                        ([key, s]: any) =>
                                          s.id ===
                                          templeteAddressSelected?.address
                                            ?.state
                                            ? s.name
                                            : ""
                                      )}{" "}
                                      -
                                      {templeteAddressSelected?.address?.state
                                        .toString()
                                        .padStart(2, "0")}
                                    </Text>
                                  </>
                                ) : (
                                  ""
                                )}

                                {currentClient?.gstin !== null && (
                                  <Text>
                                    <Text
                                      style={{
                                        fontStyle: "bold",
                                      }}
                                    >
                                      {" "}
                                      , GSTIN:{" "}
                                    </Text>
                                    {currentClient?.gstin}
                                  </Text>
                                )}

                                {currentClient?.gstin === null &&
                                  currentClient?.panNo !== null && (
                                    <Text>
                                      <Text
                                        style={{
                                          fontStyle: "bold",
                                        }}
                                      >
                                        {" "}
                                        , PAN:{" "}
                                      </Text>
                                      {currentClient?.panNo}
                                    </Text>
                                  )}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Voucher Title */}
                      <div>
                        <Text
                          style={{
                            fontSize: 18,
                          }}
                        >
                          {data?.voucherTitle?.name}
                        </Text>
                      </div>
                    </div>
                  </View>

                  {/* Party and Bill Detail */}
                  <View>
                    <Table
                      style={{ padding: "0px 0px", border: "1px solid grey" }}
                      tdStyle={{ padding: "2px" }}
                      wrap
                    >
                      <div
                        style={{
                          width: "100%",
                        }}
                      >
                        <View style={{ width: "100%" }}>
                          <TH>
                            {/* Buyer's Name and address */}
                            <TD
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <Text style={{ textDecoration: "underline" }}>
                                Buyer (Bill To):
                              </Text>
                              <Text
                                style={{
                                  fontStyle: "bold",
                                }}
                              >
                                {data?.ledgerName}
                              </Text>
                              <Text>{data?.billingAddress}</Text>
                              {data?.shippingAddress && (
                                <>
                                  <Text
                                    style={{
                                      textDecoration: "underline",
                                      marginTop: "10px",
                                    }}
                                  >
                                    Ship To:
                                  </Text>
                                  <Text>{data?.shippingAddress}</Text>
                                </>
                              )}
                            </TD>

                            {/* Bill Detail */}
                            <TD style={style.mergedMainTDWithBorder}>
                              {/* First Row - Bill Number, Date and Reference */}
                              <TR style={style.mergedMainRow}>
                                <TD
                                  weighting={0.4}
                                  style={{
                                    fontStyle: "bold",
                                    marginBottom: 0,
                                  }}
                                >
                                  Bill No:
                                </TD>
                                <TD
                                  style={{
                                    fontStyle: "bold",
                                    marginBottom: 0,
                                  }}
                                >
                                  Date
                                </TD>
                                <TD
                                  style={{
                                    fontStyle: "bold",
                                    marginBottom: 0,
                                  }}
                                >
                                  Reference No.
                                </TD>
                              </TR>
                              <TR style={style.mergedMainRow}>
                                <TD
                                  weighting={0.4}
                                  style={{
                                    marginBottom: 0,
                                  }}
                                >
                                  {data?.number}
                                </TD>
                                <TD
                                  style={{
                                    marginBottom: 0,
                                  }}
                                >
                                  {data?.date}
                                </TD>
                                <TD
                                  style={{
                                    marginBottom: 0,
                                  }}
                                >
                                  {data?.name ? data?.name : "-"}
                                </TD>
                              </TR>
                              {/* Second Row -  Place of Supply and Mode/Terms of Payment*/}
                              <TR style={style.mergedMainRow}>
                                <TD
                                  style={{
                                    fontStyle: "bold",
                                    marginBottom: 0,
                                  }}
                                  weighting={0.392}
                                >
                                  Origin of Supply
                                </TD>
                                <TD
                                  style={{
                                    fontStyle: "bold",
                                    marginBottom: 0,
                                  }}
                                >
                                  Mode/Terms of Payment
                                </TD>
                              </TR>
                              <TR style={style.mergedMainRow}>
                                <TD
                                  weighting={0.392}
                                  style={{
                                    marginBottom: 0,
                                  }}
                                >
                                  <Text>
                                    {data?.supplyState !== null ? (
                                      <>
                                        <Text>
                                          {data?.supplyState
                                            ?.toString()
                                            .padStart(2, "0")}
                                          -
                                          {Object.entries(stateOptions)?.map(
                                            ([key, s]: any) =>
                                              s.id === data?.supplyState
                                                ? s.name
                                                : ""
                                          )}
                                        </Text>
                                      </>
                                    ) : (
                                      ""
                                    )}
                                  </Text>
                                </TD>
                                <TD
                                  style={{
                                    marginBottom: 0,
                                  }}
                                >
                                  {""}
                                </TD>
                              </TR>
                              {/* Third Row - Terms & Conditions
                              {showData?.termsndConditionTS?.tnCPosition ===
                                TnC_Position.TopRight && (
                                <>
                                  <div
                                    style={{
                                      width: "100%",
                                      fontFamily:
                                        showData?.termsndConditionTS?.font
                                          ?.fontFamily,
                                      fontSize:
                                        showData?.termsndConditionTS?.font
                                          ?.fontSize,
                                    }}
                                  >
                                    <TR style={style.mergedMainRow}>
                                      <TD
                                        style={{
                                          fontStyle: "bold",
                                          marginBottom: 0,
                                        }}
                                      >
                                        Terms & Conditions
                                      </TD>
                                    </TR>
                                    <TR style={style.mergedMainRow}>
                                      <TD
                                        style={{
                                          marginBottom: 0,
                                          borderBottom: 0,
                                        }}
                                      >
                                        {data?.termsNConditions
                                          ? data?.termsNConditions
                                          : "Lorem ipsum is a dummy or placeholder text commonly used in graphic design, publishing, and web development to fill empty spaces in a layout that does not yet have content."}
                                      </TD>
                                    </TR>
                                  </div>
                                </>
                              )} */}

                              <TR style={style.mergedMainRow}>
                                <TD
                                  style={{
                                    fontStyle: "bold",
                                    marginBottom: 0,
                                    borderBottom: 0,
                                  }}
                                  weighting={0.392}
                                >
                                  Invoice No.
                                </TD>
                                <TD
                                  style={{ marginBottom: 0, borderBottom: 0 }}
                                >
                                  -
                                </TD>
                              </TR>
                              <TR style={style.mergedMainRow}>
                                <TD
                                  weighting={0.392}
                                  style={{
                                    marginBottom: 0,
                                    borderBottom: 0,
                                    fontStyle: "bold",
                                  }}
                                >
                                  Invoice Date
                                </TD>
                                <TD
                                  style={{ marginBottom: 0, borderBottom: 0 }}
                                >
                                  -
                                </TD>
                              </TR>
                            </TD>
                          </TH>
                        </View>
                      </div>
                    </Table>
                  </View>
                  {/* Items Detail */}
                  <View>
                    <Table
                      style={{
                        marginTop: "5px",
                        border: "1px solid grey",
                        fontSize: 9,
                      }}
                      tdStyle={{ padding: "2px 3px" }}
                      wrap={false}
                    >
                      <TH
                        style={{
                          width: "100%",
                          fontStyle: "bold",
                        }}
                      >
                        <TD weighting={0.05}>S.No</TD>
                        <TD
                          weighting={
                            data?.isGSTVoucher && data?.isStateSame
                              ? 0.3
                              : data?.isGSTVoucher && !data?.isStateSame
                              ? 0.45
                              : 0.5
                          }
                        >
                          {" "}
                          Item(s)
                        </TD>
                        <TD
                          weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                          style={{ justifyContent: "center" }}
                        >
                          Qty
                        </TD>

                        <TD
                          weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                          style={{ justifyContent: "center" }}
                        >
                          Unit
                        </TD>
                        <TD
                          weighting={data?.isGSTVoucher ? 0.1 : 0.125}
                          style={{ justifyContent: "flex-end" }}
                        >
                          <Text>Rate</Text>
                        </TD>
                        <TD
                          weighting={data?.isGSTVoucher ? 0.15 : 0.175}
                          style={{ justifyContent: "flex-end" }}
                        >
                          <Text>
                            {data?.isGSTVoucher ? "Taxable Amount" : "Amount"}
                          </Text>
                        </TD>
                        {data?.isGSTVoucher && data?.isStateSame ? (
                          <>
                            <TD weighting={0.15} style={style.mergedMainTD}>
                              <Text>CGST</Text>
                              <TR style={style.mergedMainRow}>
                                <TD
                                  weighting={0.3}
                                  style={{
                                    justifyContent: "center",
                                    marginBottom: 0,
                                  }}
                                >
                                  Rate
                                </TD>
                                <TD
                                  weighting={0.7}
                                  style={{
                                    justifyContent: "flex-end",
                                    marginBottom: 0,
                                  }}
                                >
                                  Amount
                                </TD>
                              </TR>
                            </TD>
                            <TD weighting={0.15} style={style.mergedMainTD}>
                              <Text>SGST</Text>
                              <TR style={style.mergedMainRow}>
                                <TD
                                  weighting={0.3}
                                  style={{
                                    justifyContent: "center",
                                    marginBottom: 0,
                                  }}
                                >
                                  Rate
                                </TD>
                                <TD
                                  weighting={0.7}
                                  style={{
                                    justifyContent: "flex-end",
                                    marginBottom: 0,
                                  }}
                                >
                                  Amount
                                </TD>
                              </TR>
                            </TD>
                          </>
                        ) : data?.isGSTVoucher && !data?.isStateSame ? (
                          <TD weighting={0.15} style={style.mergedMainTD}>
                            <Text>IGST</Text>
                            <TR style={style.mergedMainRow}>
                              <TD
                                weighting={0.3}
                                style={{
                                  justifyContent: "center",
                                  marginBottom: 0,
                                }}
                              >
                                Rate
                              </TD>
                              <TD
                                weighting={0.7}
                                style={{
                                  justifyContent: "flex-end",
                                  marginBottom: 0,
                                }}
                              >
                                Amount
                              </TD>
                            </TR>
                          </TD>
                        ) : (
                          <></>
                        )}
                      </TH>

                      {data?.items?.map((itm: any, i: any) => {
                        return (
                          <>
                            <TR key={i} style={{ width: "100%" }}>
                              <TD
                                weighting={0.05}
                                style={{ borderBottom: 0, borderTop: 0 }}
                              >
                                {itm.id !== "" ? i + 1 : ""}
                              </TD>
                              <TD
                                weighting={
                                  data?.isGSTVoucher && data?.isStateSame
                                    ? 0.3
                                    : data?.isGSTVoucher && !data?.isStateSame
                                    ? 0.45
                                    : 0.5
                                }
                                style={{ borderBottom: 0, borderTop: 0 }}
                              >
                                <Text>{itm.name}</Text>
                                <Text
                                  style={{
                                    fontSize: 8,
                                  }}
                                >
                                  {itm?.description}
                                </Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                                style={{
                                  justifyContent: "center",
                                  borderBottom: 0,
                                  borderTop: 0,
                                }}
                              >
                                <Text>{itm.amount?.qty}</Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                                style={{
                                  justifyContent: "center",
                                  borderBottom: 0,
                                  borderTop: 0,
                                }}
                              >
                                <Text>{itm.amount?.unit?.name}</Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.1 : 0.125}
                                style={{
                                  justifyContent: "flex-end",
                                  borderBottom: 0,
                                  borderTop: 0,
                                }}
                              >
                                <Text>
                                  {itm.amount?.price &&
                                    Utils.getFormattedNumber(itm.amount?.price)}
                                </Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.15 : 0.175}
                                style={{
                                  justifyContent: "flex-end",
                                  borderBottom: 0,
                                  borderTop: 0,
                                }}
                              >
                                <Text>
                                  {itm.amount?.grossAmount
                                    ? Utils.getFormattedNumber(
                                        itm.amount?.grossAmount
                                      )
                                    : " "}
                                </Text>
                              </TD>

                              {data?.isGSTVoucher && data?.isStateSame ? (
                                <>
                                  <TD
                                    weighting={0.15}
                                    style={
                                      style.mergedMainTDWithoutTopnBottomBorder
                                    }
                                  >
                                    <TR
                                      style={{
                                        ...style.mergedMainRowWithoutTopnBottomBorder,
                                        alignItems: "stretch",
                                        flex: "auto",
                                      }}
                                    >
                                      <TD
                                        weighting={0.3}
                                        style={{
                                          justifyContent: "center",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                        }}
                                      >
                                        <Text>
                                          {itm?.amount?.tax?.rate
                                            ? itm?.amount?.tax?.rate / 2
                                            : " "}
                                        </Text>
                                      </TD>
                                      <TD
                                        weighting={0.7}
                                        style={{
                                          justifyContent: "flex-end",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                        }}
                                      >
                                        <Text>
                                          {itm?.amount?.itemTaxAmt
                                            ? Utils.getFormattedNumber(
                                                itm?.amount?.itemTaxAmt / 2
                                              )
                                            : " "}
                                        </Text>
                                      </TD>
                                    </TR>
                                  </TD>
                                  <TD
                                    style={
                                      style.mergedMainTDWithoutTopnBottomBorder
                                    }
                                    weighting={0.15}
                                  >
                                    <TR
                                      style={{
                                        ...style.mergedMainRowWithoutTopnBottomBorder,
                                        alignItems: "stretch",
                                        flex: "auto",
                                      }}
                                    >
                                      <TD
                                        weighting={0.3}
                                        style={{
                                          justifyContent: "center",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                        }}
                                      >
                                        {itm?.amount?.tax?.rate &&
                                          itm?.amount?.tax?.rate / 2}
                                      </TD>
                                      <TD
                                        weighting={0.7}
                                        style={{
                                          justifyContent: "flex-end",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                        }}
                                      >
                                        {" "}
                                        {itm?.amount?.itemTaxAmt
                                          ? Utils.getFormattedNumber(
                                              itm?.amount?.itemTaxAmt / 2
                                            )
                                          : " "}
                                      </TD>
                                    </TR>
                                  </TD>
                                </>
                              ) : data?.isGSTVoucher && !data?.isStateSame ? (
                                <TD
                                  weighting={0.15}
                                  style={
                                    style.mergedMainTDWithoutTopnBottomBorder
                                  }
                                >
                                  <TR
                                    style={{
                                      ...style.mergedMainRowWithoutTopnBottomBorder,
                                      alignItems: "stretch",
                                      flex: "auto",
                                    }}
                                  >
                                    <TD
                                      weighting={0.3}
                                      style={{
                                        justifyContent: "center",
                                        marginBottom: 0,
                                        borderBottom: 0,
                                        borderTop: 0,
                                      }}
                                    >
                                      {itm?.amount?.tax?.rate
                                        ? itm?.amount?.tax?.rate / 2
                                        : " "}
                                    </TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        justifyContent: "flex-end",
                                        marginBottom: 0,
                                        borderBottom: 0,
                                        borderTop: 0,
                                      }}
                                    >
                                      {itm.amount?.itemTaxAmt
                                        ? Utils.getFormattedNumber(
                                            itm.amount?.itemTaxAmt
                                          )
                                        : " "}
                                    </TD>
                                  </TR>
                                </TD>
                              ) : (
                                <></>
                              )}
                            </TR>
                          </>
                        );
                      })}
                      <TR style={{ width: "100%", fontStyle: "bold" }}>
                        <TD weighting={0.05}></TD>
                        <TD
                          weighting={
                            data?.isGSTVoucher && data?.isStateSame
                              ? 0.3
                              : data?.isGSTVoucher && !data?.isStateSame
                              ? 0.45
                              : 0.5
                          }
                          style={{ justifyContent: "flex-end" }}
                        >
                          <Text>
                            {data?.freightInsLedgers?.length > 0
                              ? "Items Total (A)"
                              : "Total"}
                          </Text>
                        </TD>
                        <TD
                          weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                          style={{ justifyContent: "center" }}
                        >
                          <Text>{data?.totalQty}</Text>
                        </TD>
                        <TD weighting={data?.isGSTVoucher ? 0.05 : 0.075}>
                          <Text></Text>
                        </TD>
                        <TD weighting={data?.isGSTVoucher ? 0.1 : 0.125}>
                          <Text></Text>
                        </TD>
                        <TD
                          weighting={data?.isGSTVoucher ? 0.15 : 0.175}
                          style={{ justifyContent: "flex-end" }}
                        >
                          <Text>
                            {Utils.getFormattedNumber(data?.grandTaxableAmount)}
                          </Text>
                        </TD>
                        {data?.isGSTVoucher && data?.isStateSame ? (
                          <>
                            <TD weighting={0.15} style={style.mergedMainTD}>
                              <TR style={style.mergedMainRow}>
                                <TD weighting={0.3}></TD>
                                <TD
                                  weighting={0.7}
                                  style={{
                                    justifyContent: "flex-end",
                                    marginBottom: 0,
                                  }}
                                >
                                  {Utils.getFormattedNumber(
                                    data?.grandCGSTAmount
                                  )}
                                </TD>
                              </TR>
                            </TD>
                            <TD weighting={0.15} style={style.mergedMainTD}>
                              <TR style={style.mergedMainRow}>
                                <TD weighting={0.3}></TD>
                                <TD
                                  weighting={0.7}
                                  style={{
                                    justifyContent: "flex-end",
                                    marginBottom: 0,
                                  }}
                                >
                                  {Utils.getFormattedNumber(
                                    data?.grandSGSTAmount
                                  )}
                                </TD>
                              </TR>
                            </TD>
                          </>
                        ) : data?.isGSTVoucher && !data?.isStateSame ? (
                          <TD weighting={0.15} style={style.mergedMainTD}>
                            <TR style={style.mergedMainRow}>
                              <TD weighting={0.3}></TD>
                              <TD
                                weighting={0.7}
                                style={{
                                  justifyContent: "flex-end",
                                  marginBottom: 0,
                                }}
                              >
                                {Utils.getFormattedNumber(
                                  data?.grandIGSTAmount
                                )}
                              </TD>
                            </TR>
                          </TD>
                        ) : (
                          <></>
                        )}
                      </TR>
                      {/* Freight and Insurance ledgers Detail */}
                      {data?.freightInsLedgers?.length > 0 &&
                        data?.freightInsLedgers?.map((led: any, i: number) => {
                          return (
                            <TR
                              key={led.id}
                              style={{
                                width: "100%",
                                height: "auto",
                              }}
                            >
                              <TD
                                weighting={0.05}
                                style={{
                                  borderBottom: 0,
                                  borderTop: 0,
                                  alignItems: "flex-start",
                                }}
                              >
                                {led.id !== "" ? i + 1 : ""}
                              </TD>
                              <TD
                                weighting={
                                  data?.isGSTVoucher && data?.isStateSame
                                    ? 0.3
                                    : data?.isGSTVoucher && !data?.isStateSame
                                    ? 0.45
                                    : 0.5
                                }
                                style={{
                                  borderBottom: 0,
                                  borderTop: 0,
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "flex-start",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Text>{led.ledgerName}</Text>
                                <Text>{led?.description}</Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                                style={{
                                  justifyContent: "center",
                                  borderBottom: 0,
                                  borderTop: 0,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Text> </Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                                style={{
                                  justifyContent: "center",
                                  borderBottom: 0,
                                  borderTop: 0,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Text> </Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.1 : 0.125}
                                style={{
                                  justifyContent: "flex-end",
                                  borderBottom: 0,
                                  borderTop: 0,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Text> </Text>
                              </TD>
                              <TD
                                weighting={data?.isGSTVoucher ? 0.15 : 0.175}
                                style={{
                                  justifyContent: "flex-end",
                                  borderBottom: 0,
                                  borderTop: 0,
                                  alignItems: "flex-start",
                                }}
                              >
                                <Text>
                                  {led.amount
                                    ? Utils.getFormattedNumber(
                                        Math.abs(led.amount)
                                      )
                                    : " "}
                                </Text>
                              </TD>
                              {data?.isGSTVoucher && data?.isStateSame ? (
                                <>
                                  <TD
                                    weighting={0.15}
                                    style={{
                                      ...style.mergedMainTDWithoutTopnBottomBorder,
                                      alignItems: "stretch",
                                    }}
                                  >
                                    <TR
                                      key={i}
                                      style={{
                                        ...style.mergedMainRowWithoutTopnBottomBorder,
                                        alignItems: "stretch",
                                        flex: 1,
                                      }}
                                    >
                                      <TD
                                        weighting={0.3}
                                        style={{
                                          justifyContent: "center",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                          alignItems: "stretch",
                                          paddingTop: "3px",
                                          height: "100%",
                                        }}
                                      >
                                        <Text>
                                          {led.gstRate
                                            ? led.gstRate?.rate / 2
                                            : " "}
                                        </Text>
                                      </TD>
                                      <TD
                                        weighting={0.7}
                                        style={{
                                          justifyContent: "flex-end",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                          alignItems: "stretch",
                                          paddingTop: "3px",
                                          height: "100%",
                                        }}
                                      >
                                        <Text>
                                          {led?.gstAmount
                                            ? Utils.getFormattedNumber(
                                                led?.gstAmount / 2
                                              )
                                            : " "}
                                        </Text>
                                      </TD>
                                    </TR>
                                  </TD>
                                  <TD
                                    style={{
                                      ...style.mergedMainTDWithoutTopnBottomBorder,
                                      alignItems: "stretch",
                                    }}
                                    weighting={0.15}
                                  >
                                    <TR
                                      style={{
                                        ...style.mergedMainRowWithoutTopnBottomBorder,
                                        alignItems: "stretch",
                                        flex: 1,
                                      }}
                                    >
                                      <TD
                                        weighting={0.3}
                                        style={{
                                          justifyContent: "center",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                          alignItems: "stretch",
                                          paddingTop: "3px",
                                          height: "100%",
                                        }}
                                      >
                                        {led?.gstRate?.rate &&
                                          led?.gstRate?.rate / 2}
                                      </TD>
                                      <TD
                                        weighting={0.7}
                                        style={{
                                          justifyContent: "flex-end",
                                          marginBottom: 0,
                                          borderBottom: 0,
                                          borderTop: 0,
                                          alignItems: "stretch",
                                          paddingTop: "3px",
                                          height: "100%",
                                        }}
                                      >
                                        {" "}
                                        {led?.gstAmount
                                          ? Utils.getFormattedNumber(
                                              led?.gstAmount / 2
                                            )
                                          : " "}
                                      </TD>
                                    </TR>
                                  </TD>
                                </>
                              ) : data?.isGSTVoucher && !data?.isStateSame ? (
                                <TD
                                  weighting={0.15}
                                  style={
                                    style.mergedMainTDWithoutTopnBottomBorder
                                  }
                                >
                                  <TR
                                    style={
                                      style.mergedMainRowWithoutTopnBottomBorder
                                    }
                                  >
                                    <TD
                                      weighting={0.3}
                                      style={{
                                        justifyContent: "center",
                                        marginBottom: 0,
                                        borderBottom: 0,
                                        borderTop: 0,
                                      }}
                                    >
                                      {led?.gstRate?.rate
                                        ? led?.gstRate?.rate / 2
                                        : " "}
                                    </TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        justifyContent: "flex-end",
                                        marginBottom: 0,
                                        borderBottom: 0,
                                        borderTop: 0,
                                      }}
                                    >
                                      {led.gstAmount
                                        ? Utils.getFormattedNumber(
                                            led.gstAmount
                                          )
                                        : " "}
                                    </TD>
                                  </TR>
                                </TD>
                              ) : (
                                <></>
                              )}
                            </TR>
                          );
                        })}
                      {data?.freightInsLedgers?.length > 0 && (
                        <>
                          {/* other Expenses Total (B) */}
                          <TR style={{ width: "100%", fontStyle: "bold" }}>
                            <TD weighting={0.05}></TD>
                            <TD
                              weighting={
                                data?.isGSTVoucher && data?.isStateSame
                                  ? 0.3
                                  : data?.isGSTVoucher && !data?.isStateSame
                                  ? 0.45
                                  : 0.5
                              }
                              style={{ justifyContent: "flex-end" }}
                            >
                              <Text>Other Expenses Total (B)</Text>
                            </TD>
                            <TD
                              weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                              style={{ justifyContent: "center" }}
                            >
                              <Text> </Text>
                            </TD>
                            <TD weighting={data?.isGSTVoucher ? 0.05 : 0.075}>
                              <Text></Text>
                            </TD>
                            <TD weighting={data?.isGSTVoucher ? 0.1 : 0.125}>
                              <Text></Text>
                            </TD>
                            <TD
                              weighting={data?.isGSTVoucher ? 0.15 : 0.175}
                              style={{ justifyContent: "flex-end" }}
                            >
                              <Text>
                                {Utils.getFormattedNumber(
                                  data?.otherExpTotalTaxableAmount
                                )}
                              </Text>
                            </TD>
                            {data?.isGSTVoucher && data?.isStateSame ? (
                              <>
                                <TD weighting={0.15} style={style.mergedMainTD}>
                                  <TR style={style.mergedMainRow}>
                                    <TD weighting={0.3}></TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        justifyContent: "flex-end",
                                        marginBottom: 0,
                                      }}
                                    >
                                      {Utils.getFormattedNumber(
                                        data?.otherExpTotalTaxAmt / 2
                                      )}
                                    </TD>
                                  </TR>
                                </TD>
                                <TD weighting={0.15} style={style.mergedMainTD}>
                                  <TR style={style.mergedMainRow}>
                                    <TD weighting={0.3}></TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        justifyContent: "flex-end",
                                        marginBottom: 0,
                                      }}
                                    >
                                      {Utils.getFormattedNumber(
                                        data?.otherExpTotalTaxAmt / 2
                                      )}
                                    </TD>
                                  </TR>
                                </TD>
                              </>
                            ) : data?.isGSTVoucher && !data?.isStateSame ? (
                              <TD weighting={0.15} style={style.mergedMainTD}>
                                <TR style={style.mergedMainRow}>
                                  <TD weighting={0.3}></TD>
                                  <TD
                                    weighting={0.7}
                                    style={{
                                      justifyContent: "flex-end",
                                      marginBottom: 0,
                                    }}
                                  >
                                    {Utils.getFormattedNumber(
                                      data?.otherExpTotalTaxAmt
                                    )}
                                  </TD>
                                </TR>
                              </TD>
                            ) : (
                              <></>
                            )}
                          </TR>
                          {/* Items + other Expenses Total (A+B) */}
                          <TR style={{ width: "100%", fontStyle: "bold" }}>
                            <TD weighting={0.05}></TD>
                            <TD
                              weighting={
                                data?.isGSTVoucher && data?.isStateSame
                                  ? 0.3
                                  : data?.isGSTVoucher && !data?.isStateSame
                                  ? 0.45
                                  : 0.5
                              }
                              style={{ justifyContent: "flex-end" }}
                            >
                              <Text>Total (A+B)</Text>
                            </TD>
                            <TD
                              weighting={data?.isGSTVoucher ? 0.05 : 0.075}
                              style={{ justifyContent: "center" }}
                            >
                              <Text> </Text>
                            </TD>
                            <TD weighting={data?.isGSTVoucher ? 0.05 : 0.075}>
                              <Text></Text>
                            </TD>
                            <TD weighting={data?.isGSTVoucher ? 0.1 : 0.125}>
                              <Text></Text>
                            </TD>
                            <TD
                              weighting={data?.isGSTVoucher ? 0.15 : 0.175}
                              style={{ justifyContent: "flex-end" }}
                            >
                              <Text>
                                {Utils.getFormattedNumber(
                                  data?.grandTaxableAmount
                                )}
                              </Text>
                            </TD>
                            {data?.isGSTVoucher && data?.isStateSame ? (
                              <>
                                <TD weighting={0.15} style={style.mergedMainTD}>
                                  <TR style={style.mergedMainRow}>
                                    <TD weighting={0.3}></TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        justifyContent: "flex-end",
                                        marginBottom: 0,
                                      }}
                                    >
                                      {Utils.getFormattedNumber(
                                        data?.grandCGSTAmount
                                      )}
                                    </TD>
                                  </TR>
                                </TD>
                                <TD weighting={0.15} style={style.mergedMainTD}>
                                  <TR style={style.mergedMainRow}>
                                    <TD weighting={0.3}></TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        justifyContent: "flex-end",
                                        marginBottom: 0,
                                      }}
                                    >
                                      {Utils.getFormattedNumber(
                                        data?.grandSGSTAmount
                                      )}
                                    </TD>
                                  </TR>
                                </TD>
                              </>
                            ) : data?.isGSTVoucher && !data?.isStateSame ? (
                              <TD weighting={0.15} style={style.mergedMainTD}>
                                <TR style={style.mergedMainRow}>
                                  <TD weighting={0.3}></TD>
                                  <TD
                                    weighting={0.7}
                                    style={{
                                      justifyContent: "flex-end",
                                      marginBottom: 0,
                                    }}
                                  >
                                    {Utils.getFormattedNumber(
                                      data?.grandIGSTAmount
                                    )}
                                  </TD>
                                </TR>
                              </TD>
                            ) : (
                              <></>
                            )}
                          </TR>
                        </>
                      )}
                      {/* Gross Amount Detail */}
                      {data?.roundOff?.roundOffAmount !== 0 && (
                        <>
                          {" "}
                          <TR
                            style={{
                              width: "100%",
                              fontStyle: "bold",
                            }}
                          >
                            <TD
                              weighting={data?.isGSTVoucher ? 0.464 : 0.535}
                              style={{
                                justifyContent: "flex-end",
                              }}
                            >
                              Gross Total
                            </TD>
                            <TD style={{ justifyContent: "flex-end" }}>
                              <Text>
                                {Utils.getFormattedNumber(
                                  data?.cashDiscount
                                    ? data?.amountSummary
                                        ?.voucherAmountWithoutTCS +
                                        Math.abs(
                                          data?.cashDiscount?.discountAmount
                                        )
                                    : data?.amountSummary
                                        ?.voucherAmountWithoutTCS
                                )}
                              </Text>
                            </TD>
                          </TR>
                        </>
                      )}

                      {data?.roundOff?.roundOffAmount !== 0 && (
                        <>
                          {" "}
                          <TR
                            style={{
                              width: "100%",
                            }}
                          >
                            <TD
                              weighting={data?.isGSTVoucher ? 0.464 : 0.535}
                              style={{
                                justifyContent: "flex-end",
                              }}
                            >
                              Round Off
                            </TD>
                            <TD style={{ justifyContent: "flex-end" }}>
                              <Text>
                                {Utils.getFormattedNumber(
                                  data?.roundOff?.roundOffAmount
                                )}
                              </Text>
                            </TD>
                          </TR>
                        </>
                      )}

                      {data?.cashDiscount && (
                        <>
                          {" "}
                          <TR
                            style={{
                              width: "100%",
                            }}
                          >
                            <TD
                              weighting={data?.isGSTVoucher ? 0.464 : 0.535}
                              style={{
                                justifyContent: "flex-end",
                              }}
                            >
                              Cash Discount
                            </TD>
                            <TD style={{ justifyContent: "flex-end" }}>
                              <Text>
                                {Utils.getFormattedNumber(
                                  data?.cashDiscount?.discountAmount
                                )}
                              </Text>
                            </TD>
                          </TR>
                        </>
                      )}
                      {/* Grand Total */}
                      <TR
                        style={{
                          width: "100%",
                          fontSize: 12,
                          fontStyle: "bold",
                        }}
                      >
                        <TD
                          weighting={0.464}
                          style={{
                            justifyContent: "flex-end",
                          }}
                        >
                          Total Bill Value
                        </TD>
                        <TD style={{ justifyContent: "flex-end" }}>
                          <Text style={{}}>
                            ₹
                            {Utils.getFormattedNumber(data?.totalInvoiceAmount)}
                          </Text>
                        </TD>
                      </TR>
                      <TR style={{ width: "100%" }}>
                        <TD
                          style={{
                            display: "flex",
                            flexDirection: "row",
                          }}
                        >
                          <Text
                            style={{
                              fontStyle: "bold",
                            }}
                          >
                            Amount (in words):
                          </Text>
                          <Text>
                            {" INR "}
                            {data?.totalInvoiceAmountInWords}
                          </Text>
                        </TD>
                      </TR>
                    </Table>
                  </View>
                  {/* Payment received and voucher balance detail  */}
                  <View>
                    <Table
                      wrap={false}
                      tdStyle={{ padding: "2px" }}
                      style={{
                        marginTop: "5px",
                        width: "100%",
                        border: "1px solid grey",
                      }}
                    >
                      <div>
                        <TR>
                          <TD
                            weighting={0.5}
                            style={{
                              justifyContent: "center",
                              paddingRight: "4px",
                            }}
                          >
                            Paid Amount
                          </TD>
                          <TD
                            weighting={0.5}
                            style={{
                              justifyContent: "center",
                              paddingRight: "4px",
                            }}
                          >
                            {Utils.getFormattedNumber(
                              data?.amountSummary?.receivedAmount
                            )}
                          </TD>
                          <TD
                            weighting={0.5}
                            style={{
                              justifyContent: "center",
                              paddingRight: "4px",
                            }}
                          >
                            Due Amount
                          </TD>
                          <TD
                            weighting={0.5}
                            style={{
                              justifyContent: "center",
                              paddingRight: "4px",
                            }}
                          >
                            {Utils.getFormattedNumber(
                              data?.amountSummary?.dueAmount
                            )}
                          </TD>
                        </TR>
                      </div>
                    </Table>
                  </View>
                  {/* Instant payment detail */}
                  {data?.instantPayment && (
                    <View>
                      <div style={{ width: "60%" }}>
                        <div>
                          <Text style={{ marginBottom: "2px" }}>
                            {props.type === Voucher_Type.PurchaseVoucher
                              ? "Payment made by: "
                              : "Amount received in: "}
                          </Text>
                        </div>
                        <Table
                          wrap={false}
                          tdStyle={{ padding: "2px" }}
                          style={{ paddingBottom: "5px", width: "100%" }}
                        >
                          <TH
                            style={{
                              fontStyle: "bold",
                            }}
                          >
                            <TD
                              style={style.summaryTableStyleLeft}
                              weighting={0.4}
                            >
                              Account Name
                            </TD>
                            <TD
                              style={style.summaryTableStyleCenter}
                              weighting={0.3}
                            >
                              Voucher No.
                            </TD>
                            <TD
                              style={style.summaryTableStyleRight}
                              weighting={0.3}
                            >
                              Amount
                            </TD>
                          </TH>
                          {data?.payRecVouchers?.map((vch: any) => (
                            <TR>
                              <TD
                                style={style.summaryTableStyleLeft}
                                weighting={0.4}
                              >
                                {vch.effectiveLedger[0].ledgerName}
                              </TD>
                              <TD
                                style={style.summaryTableStyleCenter}
                                weighting={0.3}
                              >
                                {vch.number}
                              </TD>
                              <TD
                                style={style.summaryTableStyleRight}
                                weighting={0.3}
                              >
                                {Utils.getFormattedNumber(
                                  Math.abs(vch.effectiveLedger[0].amount)
                                )}
                              </TD>
                            </TR>
                          ))}
                          <TH
                            style={{
                              fontStyle: "bold",
                            }}
                          >
                            <TD
                              style={style.summaryTableStyleLeft}
                              weighting={0.4}
                            >
                              Total Amount
                            </TD>
                            <TD
                              style={style.summaryTableStyleCenter}
                              weighting={0.3}
                            ></TD>
                            <TD
                              style={style.summaryTableStyleRight}
                              weighting={0.3}
                            >
                              {Utils.getFormattedNumber(
                                Math.abs(
                                  data?.payRecVouchers?.reduce(
                                    (acc: any, rec: any) =>
                                      acc +
                                      (rec?.amountSummary?.totalInvoiceAmount >
                                      0
                                        ? rec?.amountSummary?.totalInvoiceAmount
                                        : 0),
                                    0
                                  )
                                )
                              )}
                            </TD>
                          </TH>
                        </Table>
                      </div>
                    </View>
                  )}
                  {/* Tax Summary */}
                  <View>
                    <Table
                      style={{
                        border: "1px solid grey",
                        marginTop: "5px",
                      }}
                      wrap={false}
                      tdStyle={{ padding: "2px" }}
                    >
                      <div style={{ fontSize: 9 }}>
                        <TR style={{ fontStyle: "bold" }}>
                          <TD
                            style={{
                              justifyContent: "flex-end",
                            }}
                          >
                            Taxable value
                          </TD>
                          <TD
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: 0,
                              borderBottom: "none",
                            }}
                          >
                            <Text>IGST</Text>
                            <TR
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                justifyContent: "flex-end",
                                borderBottom: "none",
                                padding: 0,
                              }}
                            >
                              <TD
                                weighting={0.3}
                                style={{
                                  justifyContent: "center",
                                  marginTop: 0,
                                  marginBottom: 0,
                                }}
                              >
                                Rate
                              </TD>
                              <TD
                                weighting={0.7}
                                style={{
                                  justifyContent: "flex-end",
                                  marginTop: 0,
                                  marginBottom: 0,
                                }}
                              >
                                Amount
                              </TD>
                            </TR>
                          </TD>
                          <TD
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: 0,
                              borderBottom: "none",
                            }}
                          >
                            <Text>CGST</Text>
                            <TR
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                justifyContent: "flex-end",
                                borderBottom: "none",
                                padding: 0,
                              }}
                            >
                              <TD
                                style={{
                                  marginTop: 0,
                                  marginBottom: 0,
                                  justifyContent: "center",
                                }}
                                weighting={0.3}
                              >
                                Rate
                              </TD>
                              <TD
                                style={{
                                  marginTop: 0,
                                  marginBottom: 0,
                                  justifyContent: "flex-end",
                                }}
                                weighting={0.7}
                              >
                                Amount
                              </TD>
                            </TR>
                          </TD>
                          <TD
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: 0,
                              borderBottom: "none",
                            }}
                          >
                            <Text>SGST/UTGST</Text>
                            <TR
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                justifyContent: "flex-end",
                                borderBottom: "none",
                                padding: 0,
                              }}
                            >
                              <TD
                                style={{
                                  marginTop: 0,
                                  marginBottom: 0,
                                  justifyContent: "center",
                                }}
                                weighting={0.3}
                              >
                                Rate
                              </TD>
                              <TD
                                style={{
                                  marginTop: 0,
                                  marginBottom: 0,
                                  justifyContent: "flex-end",
                                }}
                                weighting={0.7}
                              >
                                Amountt
                              </TD>
                            </TR>
                          </TD>
                          <TD
                            style={{
                              justifyContent: "flex-end",
                            }}
                          >
                            Total Tax Amount
                          </TD>
                        </TR>
                        {data?.taxDetail?.map((taxAmt: any, i: any) => {
                          return (
                            <div>
                              <TR key={i} style={{ width: "100%" }}>
                                <TD
                                  style={{
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  {Utils.getFormattedNumber(
                                    taxAmt.taxableAmount
                                  )}
                                </TD>
                                <TD
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: 0,
                                    borderBottom: "none",
                                  }}
                                >
                                  <TR
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      width: "100%",
                                      justifyContent: "flex-end",
                                      borderBottom: "none",
                                      padding: 0,
                                    }}
                                  >
                                    <TD
                                      weighting={0.3}
                                      style={{
                                        justifyContent: "center",
                                        marginBottom: 0,
                                      }}
                                    >
                                      {taxAmt.igstRate}
                                    </TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        justifyContent: "flex-end",
                                        marginBottom: 0,
                                      }}
                                    >
                                      {Utils.getFormattedNumber(taxAmt.igstAmt)}
                                    </TD>
                                  </TR>
                                </TD>
                                <TD
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: 0,
                                    borderBottom: "none",
                                  }}
                                >
                                  <TR
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      width: "100%",
                                      justifyContent: "flex-end",
                                      borderBottom: "none",
                                      padding: 0,
                                    }}
                                  >
                                    <TD
                                      weighting={0.3}
                                      style={{
                                        marginBottom: 0,
                                        justifyContent: "center",
                                      }}
                                    >
                                      {taxAmt.cgstRate}
                                    </TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        marginBottom: 0,
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      {Utils.getFormattedNumber(taxAmt.cgstAmt)}
                                    </TD>
                                  </TR>
                                </TD>
                                <TD
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: 0,
                                    borderBottom: "none",
                                  }}
                                >
                                  <TR
                                    style={{
                                      display: "flex",
                                      flexDirection: "row",
                                      width: "100%",
                                      justifyContent: "flex-end",
                                      borderBottom: "none",
                                      padding: 0,
                                    }}
                                  >
                                    <TD
                                      weighting={0.3}
                                      style={{
                                        marginBottom: 0,
                                        justifyContent: "center",
                                      }}
                                    >
                                      {taxAmt.sgstRate}
                                    </TD>
                                    <TD
                                      weighting={0.7}
                                      style={{
                                        marginBottom: 0,
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      {Utils.getFormattedNumber(taxAmt.sgstAmt)}
                                    </TD>
                                  </TR>
                                </TD>
                                <TD
                                  style={{
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  {Utils.getFormattedNumber(taxAmt.totalTaxAmt)}
                                </TD>
                              </TR>
                            </div>
                          );
                        })}
                        <TR
                          style={{
                            fontStyle: "bold",
                            width: "100%",
                          }}
                        >
                          <TD
                            style={{
                              justifyContent: "flex-end",
                            }}
                          >
                            Total
                          </TD>
                          <TD
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: 0,
                              borderBottom: "none",
                            }}
                          >
                            <TR
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                justifyContent: "flex-end",
                                borderBottom: "none",
                                padding: 0,
                              }}
                            >
                              <TD
                                weighting={0.3}
                                style={{
                                  justifyContent: "center",
                                  marginBottom: 0,
                                }}
                              >
                                -
                              </TD>
                              <TD
                                weighting={0.7}
                                style={{
                                  justifyContent: "flex-end",
                                  marginBottom: 0,
                                }}
                              >
                                {Utils.getFormattedNumber(
                                  data?.grandIGSTAmount
                                )}
                              </TD>
                            </TR>
                          </TD>
                          <TD
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: 0,
                              borderBottom: "none",
                            }}
                          >
                            <TR
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                justifyContent: "flex-end",
                                borderBottom: "none",
                                padding: 0,
                              }}
                            >
                              <TD
                                weighting={0.3}
                                style={{
                                  marginBottom: 0,
                                  justifyContent: "center",
                                }}
                              >
                                -
                              </TD>
                              <TD
                                weighting={0.7}
                                style={{
                                  marginBottom: 0,
                                  justifyContent: "flex-end",
                                }}
                              >
                                {Utils.getFormattedNumber(
                                  data?.grandCGSTAmount
                                )}
                              </TD>
                            </TR>
                          </TD>
                          <TD
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: 0,
                              borderBottom: "none",
                            }}
                          >
                            <TR
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                width: "100%",
                                justifyContent: "flex-end",
                                borderBottom: "none",
                                padding: 0,
                              }}
                            >
                              <TD
                                weighting={0.3}
                                style={{
                                  marginBottom: 0,
                                  justifyContent: "center",
                                }}
                              >
                                -
                              </TD>
                              <TD
                                weighting={0.7}
                                style={{
                                  marginBottom: 0,
                                  justifyContent: "flex-end",
                                }}
                              >
                                {Utils.getFormattedNumber(
                                  data?.grandSGSTAmount
                                )}
                              </TD>
                            </TR>
                          </TD>
                          <TD
                            style={{
                              justifyContent: "flex-end",
                            }}
                          >
                            {Utils.getFormattedNumber(data?.grandTotalAmount)}
                          </TD>
                        </TR>
                      </div>
                    </Table>
                  </View>

                  {/* Terms & Condition in Bottom */}

                  <View>
                    <Table
                      style={{
                        border: "none",
                        marginTop: "5px",
                      }}
                      tdStyle={{ padding: "0px" }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                        }}
                      >
                        <TR style={style.widths}>
                          <TD
                            style={{
                              // paddingLeft: "5px",
                              fontStyle: "bold",
                            }}
                          >
                            Terms & Conditions
                          </TD>
                        </TR>
                        <TR>
                          <TD style={{ padding: "0px 8px" }}>
                            {data?.termsNConditions
                              ? (() => {
                                  const parsedContent = parse(
                                    data?.termsNConditions?.tnCText
                                  );

                                  // Check if parsedContent is an array and handle accordingly
                                  if (Array.isArray(parsedContent)) {
                                    return (
                                      <div>
                                        {
                                          parsedContent.map((content: any) => (
                                            <View
                                              style={{
                                                width: "100%",
                                              }}
                                            >
                                              <Text>{renderNode(content)}</Text>
                                            </View>
                                          )) // Handle array of nodes
                                        }
                                      </div>
                                    );
                                  } else {
                                    return renderNode(parsedContent); // Handle single node
                                  }
                                })()
                              : "-"}{" "}
                          </TD>
                        </TR>
                      </div>
                    </Table>
                  </View>

                  {/* Disclaimer and Signature */}
                  <View>
                    <Table
                      style={{ border: "none", marginTop: "5px" }}
                      tdStyle={{ padding: "4px" }}
                      wrap={false}
                    >
                      <TR>
                        {/* Disclaimer */}
                        <TD
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div
                            style={{
                              justifyContent: "flex-start",
                              fontSize: 9,
                            }}
                          >
                            {/* <Text style={{ textDecoration: "underline" }}>
                              Disclaimer:
                            </Text>

                            <Text>
                              1. Certified that the particular given above are
                              true and correct and the amount indicated
                              represents the price actually charged and there is
                              no additional consideration flowing, directly or
                              indirectly from the buyer.
                            </Text>
                            <Text>
                              2. Subject to Jodhpur Jurisdiction only.
                            </Text> */}
                          </div>
                        </TD>
                        {/* Signature */}
                        <TD
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            gap: "42px",
                          }}
                        >
                          <div
                            style={{
                              textAlign: "end",
                              width: "100%",
                              fontSize: 12,
                            }}
                          >
                            <Text
                              style={{
                                textAlign: "right",
                                fontStyle: "bold",
                              }}
                            >
                              For {currentClient?.businessName}
                            </Text>
                          </div>
                          <div
                            style={{
                              textAlign: "end",
                              width: "100%",
                            }}
                          >
                            <Text style={{ textAlign: "right" }}>
                              (Authorized Signatory)
                            </Text>
                          </div>
                        </TD>
                      </TR>
                    </Table>
                  </View>
                </div>
              </View>
            </Page>
          </Document>
        </PDFViewer>
      </Drawer>
    </React.Fragment>
  );
};
export default PurchaseBillPDF;
